// Duplicate of AutocompleteFilter.tsx for the "List of Tenant IDs" filter.
// Keep behavior in sync with AutocompleteFilter.tsx (only FILTER_NAME and
// the title/placeholder translation keys differ).
import React, { useCallback, useEffect, useMemo } from 'react';
import { Autocomplete, AutocompleteProps, Box } from '@p81-common/ui';
import type { AutocompleteOptionProps } from '@p81-common/ui';
import { Heading } from 'components/common/Elements/Elements';
import classes from './FiltersBlock.module.scss';
import { useTranslation } from 'react-i18next';

interface TenantAutocompleteFilterProps {
  autocompleteOptionsMultiple: AutocompleteOptionProps[];
  setAutocompleteOptionsMultiple: React.Dispatch<
    React.SetStateAction<AutocompleteOptionProps[]>
  >;
  handleFilter: (e: any) => void;
}

const FILTER_NAME = 'customerId';

const TenantAutocompleteFilter = ({
  autocompleteOptionsMultiple,
  setAutocompleteOptionsMultiple,
  handleFilter
}: TenantAutocompleteFilterProps) => {
  const { t } = useTranslation(['activity']);
  useEffect(() => {
    const arrayOfValues = autocompleteOptionsMultiple.map(obj => obj.name);
    const e = {
      target: FILTER_NAME,
      value: arrayOfValues
    };
    handleFilter(e);
    // eslint-disable-next-line
  }, [autocompleteOptionsMultiple]);

  const handleAutocompleteCreate: AutocompleteProps['handleCreate'] =
    useCallback(
      (e, v) => {
        if (e.type === 'blur') return;
        const value = v[v.length - 1]
          ?.toString()
          .trim()
          .replace(/,/g, ' ')
          .replace(/\t+/g, ' ');
        const newValue = value.split(' ');
        const uniqueOptions = new Set(
          [
            ...autocompleteOptionsMultiple.map(option => option.name),
            ...newValue
          ].filter(str => str !== '')
        );
        const updatedOptions = Array.from(uniqueOptions).map(name => ({
          name,
          selected: true
        }));

        setAutocompleteOptionsMultiple(updatedOptions);
      },
      [autocompleteOptionsMultiple, setAutocompleteOptionsMultiple]
    );

  const handleAutocompleteDelete: AutocompleteProps['handleDelete'] = (
    e,
    v
  ) => {
    if ((e as React.KeyboardEvent).key === 'Enter') {
      return;
    }
    setAutocompleteOptionsMultiple(v);
  };

  const handleAutocompleteClear: AutocompleteProps['handleClear'] = () => {
    setAutocompleteOptionsMultiple([]);
  };

  const autocompleteValueMultiple = useMemo(
    () => autocompleteOptionsMultiple.filter(o => o.selected),
    [autocompleteOptionsMultiple]
  );

  return (
    <Box className={classes.autocompleteSpacing}>
      <Heading className={classes.title}>
        {t('activity:networkRollout.LIST_OF_TENANT_ID')}
      </Heading>
      <Autocomplete
        className={classes.autocomplete}
        handleClear={handleAutocompleteClear}
        handleCreate={handleAutocompleteCreate}
        handleDelete={handleAutocompleteDelete}
        value={autocompleteValueMultiple}
        options={autocompleteOptionsMultiple}
        size={'medium'}
        multiple={true}
        defaultLimitTags={-1}
        alwaysExpand={true}
        showLimitTagsTooltip={true}
        freeSolo={true}
        placeholder={
          autocompleteOptionsMultiple.length
            ? ''
            : t('activity:networkRollout.AUTOCOMPLETE_PLACEHOLDER_TENANT')
        }
      />
    </Box>
  );
};

export default TenantAutocompleteFilter;
